import { AlertMessage, MUIIcon, ProgressIndicator } from "../../../widgets";
import { ControlType, FieldFilterType, Property } from "../../../base/types";
import { useCallback, useEffect, useState } from "react";

import { Button } from "react-bootstrap";
import { DesignerMode } from "../../types";
import { FeedbackFormComponentProperties } from "./properties";
import { IControlAttributes } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useAuth } from "../../../../modules/auth";
import { useDesignContext } from "../../../providers";
import { useGetProperties } from "../../../base/hooks";
import { useLazyGetContactFeedbacksQuery } from "./feedbackApi";
import { useLazyReadMultipleQuery } from "../../../services/data/tableDataApi";

interface IFeedbackForms {
    name?: string,
    surveyUrl: string
}

export const useGetFeedbacks = (feedbackIds?: string[]) => {
    const { user, siteId } = useAuth();
    const [getRecords] = useLazyReadMultipleQuery();
    const [records, setRecords] = useState<IFeedbackForms[]>([]);

    useEffect(() => {
        if (!feedbackIds || feedbackIds.length === 0) return;

        getFeedbacks(feedbackIds);
        // eslint-disable-next-line
    }, [user, feedbackIds]);

    const getFeedbacks = (feedbackIds: string[]): Promise<IFeedbackForms[]> => {
        if (!user?.contactId) {
            return Promise.reject('User contact not found.');
        }

        const fetchRecordsPromises = feedbackIds.map(feedbackId =>
            getRecords({
                userId: user?.userId || '',
                portalId: siteId || '',
                tableId: "slcm_feedbackwindow",
                fieldList: ["slcm_name", "edtec365_surveyurl"],
                filters: [{
                    group: 0, filters: [
                        { field: "slcm_feedbackwindowid", type: FieldFilterType.Const, value: feedbackId },
                    ]
                }]
            })
                .unwrap()
        );

        return Promise.all(fetchRecordsPromises)
            .then(results => {
                const combinedRecords = results.flatMap(data =>
                    data.records.map<IFeedbackForms>(p => ({
                        name: p["slcm_name"],
                        surveyUrl: p["edtec365_surveyurl"],
                    }))
                );

                setRecords(combinedRecords);
                return Promise.resolve(combinedRecords);
            });
    };

    return { records };
};

export const FeedbackFormComponent = ({ control, className }: IControlAttributes) => {
    const { control: { designerMode } } = useDesignContext();
    const { properties } = useGetProperties({ bindings: control.bindings });
    const hidden: boolean | undefined = properties[Property.Hidden];
    const tooltip: string | undefined = properties[Property.Tooltip];
    const pageId: string | undefined = properties[Property.PageId];

    if (designerMode === DesignerMode.View && hidden === true) {
        return <></>;
    }

    return (
        <div className={className} title={tooltip}>
            <Feedbacks pageId={pageId} />
        </div>
    );
};

const Feedbacks = ({ pageId }: { pageId?: string }) => {
    const { control: { designerMode } } = useDesignContext();
    const [getServices, { data, isLoading, isSuccess, isError, error }] = useLazyGetContactFeedbacksQuery();
    const [term, setTerm] = useState("");
    const { user } = useAuth();
    const [feedbackIds, setFeedbackIds] = useState<string[]>([]);
    const { records } = useGetFeedbacks(feedbackIds);

    useEffect(() => {
        const contactId = user?.entityId || user?.contactId;
        if (!contactId) return;

        getServices(contactId);
        // eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        if (data && data.length > 0) {
            // Assuming data is an array and each item has an id
            setFeedbackIds(data.map(item => item.id));
        }
    }, [data]);

    const getApiErrorMessage = useCallback(() => {
        return (error as any)?.data?.message || "Failed to load services";
    }, [error]);

    const handleUrl = (surveyUrl: string) => {
        surveyUrl ? window.open(surveyUrl, '_blank', 'noopener,noreferrer') : <></>;
    }

    return (
        <div>
            <ProgressIndicator show={isLoading} message="Please wait.." />
            <AlertMessage type="Error" show={isError} message={getApiErrorMessage()} />
            {isSuccess && <>
                <div className="input-group input-group-sm mb-2">
                    <input type="search"
                        disabled={designerMode === DesignerMode.Draft}
                        placeholder="Search"
                        className="form-control form-control-sm py-2"
                        value={term} onChange={(e) => setTerm(e.target.value)} />
                    <span className="input-group-text py-2">
                        <MUIIcon iconName="SearchOutlined" />
                    </span>
                </div>

                <ul className="list-group">
                    {records.filter(record => (record.name?.toLowerCase() || '').includes(term.toLowerCase())).map((record, i) => (
                        <>
                            <div className="border rounded p-3 mb-3 bg-light" key={i}>
                                <div className="d-flex flex-wrap justify-content-between">
                                    <div className="p-2 flex-fill col-1">
                                        <span className="fw-bold">Name</span>
                                        <div>{record.name}</div>
                                    </div>
                                    <div className="p-2">
                                        <Button variant="success" onClick={() => handleUrl(record.surveyUrl)}>
                                            <MUIIcon iconName="FeedbackOutlined" className="me-1" />
                                            Feedback</Button>
                                    </div>
                                </div>
                            </div>
                        </>
                    ))}
                </ul>
            </>}
        </div>
    );
};

Registry.addControl({
    id: "42e639e5-47c0-4e51-8fb7-f153cca7b8e4",
    name: "Feedback Form",
    type: ControlType.FeedbackFormComponent,
    properties: FeedbackFormComponentProperties,
    component: FeedbackFormComponent
})

